import { ApplicableList, ListModel, Visibility } from "lib/models/list-models";
import { UserPermissions } from "lib/services";
import { CSRFService } from "lib/services/csrf-service";
import { ListsService } from "lib/services/lists-service";
import React, { createContext, ReactNode, useCallback, useContext, useReducer } from "react";

interface ListsState {
  lists: ListModel[];
  loading: boolean;
}

type ListsAction =
  | { type: "SET_LISTS"; payload: ListModel[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_LIST"; payload: ListModel }
  | { type: "UPDATE_LIST"; payload: ListModel }
  | { type: "DELETE_LIST"; payload: string }
  | { type: "UPDATE_LIST_ITEMS"; payload: { listId: string; itemId: string } };

interface ListsContextType {
  state: ListsState;
  getLists: (options?: {
    includeItemDetails?: boolean;
    visibility?: Visibility;
    tags?: string[];
    allowedDomains?: string[];
    hasRules?: boolean;
    owners?: string;
  }) => Promise<void>;
  getMyLists: (options?: {
    includeItemDetails?: boolean;
    visibility?: Visibility;
    tags?: string[];
    allowedDomains?: string[];
    hasRules?: boolean;
  }) => Promise<void>;
  evaluateLists: (
    attributes: Record<string, any>,
    options?: { domains?: string[]; includeItemDetails?: boolean; limit?: number }
  ) => Promise<ApplicableList[]>;
  createList: (data: {
    name: string;
    description?: string;
    visibility?: Visibility;
    allowedDomains?: string[];
    itemIds?: string[];
    evaluationRules?: DocumentType;
  }) => Promise<ListModel>;
  updateList: (
    listId: string,
    data: {
      name: string;
      description?: string;
      visibility?: Visibility;
      allowedDomains?: string[];
      evaluationRules?: DocumentType;
    }
  ) => Promise<ListModel>;
  deleteList: (listId: string) => Promise<string>;
  addItemToList: (
    taxonomyIds: string[],
    options: { listId: string } | { autoCreate: true; name: string; domain: string }
  ) => Promise<ListModel>;
  removeItemFromList: (listId: string, itemId: string) => Promise<{ listId: string; itemId: string }>;
}

const ListsContext = createContext<ListsContextType | null>(null);

const listsReducer = (state: ListsState, action: ListsAction): ListsState => {
  switch (action.type) {
    case "SET_LISTS":
      return { ...state, lists: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "ADD_LIST":
      return { ...state, lists: [...state.lists, action.payload] };
    case "UPDATE_LIST":
      return {
        ...state,
        lists: state.lists.map((list) => (list.listId === action.payload.listId ? action.payload : list)),
      };
    case "DELETE_LIST":
      return { ...state, lists: state.lists.filter((list) => list.listId !== action.payload) };
    case "UPDATE_LIST_ITEMS":
      return {
        ...state,
        lists: state.lists.map((list) =>
          list.listId === action.payload.listId
            ? {
                ...list,
                items: list.items?.filter((item) => item.taxonomyId !== action.payload.itemId) || [],
                fullItems: list.fullItems?.filter((item) => item.taxonomyId !== action.payload.itemId) || [],
                itemCount: list.itemCount - 1,
              }
            : list
        ),
      };
    default:
      return state;
  }
};

export const ListsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(listsReducer, { lists: [], loading: false });
  const listsService = new ListsService();
  const csrfService = new CSRFService();

  const getLists = useCallback(async (options = {}) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const response = await listsService.getLists(options);
      dispatch({ type: "SET_LISTS", payload: response.lists });
    } catch (error) {
      console.error("Error fetching lists:", error);
      dispatch({ type: "SET_LISTS", payload: [] });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  const getMyLists = useCallback(
    async (options = {}) => {
      if (!UserPermissions.current.username) {
        throw new Error("User authentication required to fetch lists");
      }
      await getLists({ ...options, owners: UserPermissions.current.username });
    },
    [getLists]
  );

  const evaluateLists = useCallback(
    async (
      attributes: Record<string, any>,
      options: { domains?: string[]; includeItemDetails?: boolean; limit?: number } = {}
    ) => {
      try {
        const csrfToken = await csrfService.getToken();
        const response = await listsService.evaluateLists(attributes, options, csrfToken);
        return response.applicableLists;
      } catch (error) {
        console.error("Error evaluating lists:", error);
        throw error;
      }
    },
    []
  );

  const createList = useCallback(
    async (data: {
      name: string;
      description?: string;
      visibility?: Visibility;
      allowedDomains?: string[];
      itemIds?: string[];
      evaluationRules?: DocumentType;
    }) => {
      if (!UserPermissions.current.username) {
        throw new Error("User authentication required to create lists");
      }

      try {
        const csrfToken = await csrfService.getToken();
        const response = await listsService.createList(
          {
            ...data,
            visibility: data.visibility || Visibility.PRIVATE,
          },
          csrfToken
        );
        dispatch({ type: "ADD_LIST", payload: response.list });
        return response.list;
      } catch (error) {
        console.error("Error creating list:", error);
        throw error;
      }
    },
    []
  );

  const updateList = useCallback(
    async (
      listId: string,
      data: {
        name: string;
        description?: string;
        visibility?: Visibility;
        allowedDomains?: string[];
        evaluationRules?: DocumentType;
      }
    ) => {
      try {
        const csrfToken = await csrfService.getToken();
        const response = await listsService.updateList(listId, data, csrfToken);
        dispatch({ type: "UPDATE_LIST", payload: response.list });
        return response.list;
      } catch (error) {
        console.error("Error updating list:", error);
        throw error;
      }
    },
    []
  );

  const deleteList = useCallback(async (listId: string) => {
    try {
      const csrfToken = await csrfService.getToken();
      await listsService.deleteList(listId, csrfToken);
      dispatch({ type: "DELETE_LIST", payload: listId });
      return listId;
    } catch (error) {
      console.error("Error deleting list:", error);
      throw error;
    }
  }, []);

  const addItemToList = useCallback(
    async (taxonomyIds: string[], options: { listId: string } | { autoCreate: true; name: string; domain: string }) => {
      try {
        const csrfToken = await csrfService.getToken();
        const response = await listsService.addItemToList({ itemIds: taxonomyIds, ...options }, csrfToken);
        return response.list;
      } catch (error) {
        console.error("Error adding item to list:", error);
        throw error;
      }
    },
    []
  );

  const removeItemFromList = useCallback(async (listId: string, itemId: string) => {
    try {
      const csrfToken = await csrfService.getToken();
      await listsService.removeItemFromList(listId, itemId, csrfToken);
      dispatch({ type: "UPDATE_LIST_ITEMS", payload: { listId, itemId } });
      return { listId, itemId };
    } catch (error) {
      console.error("Error removing item from list:", error);
      throw error;
    }
  }, []);

  return (
    <ListsContext.Provider
      value={{
        state,
        getLists,
        getMyLists,
        evaluateLists,
        createList,
        updateList,
        deleteList,
        addItemToList,
        removeItemFromList,
      }}
    >
      {children}
    </ListsContext.Provider>
  );
};

export const useLists = () => {
  const context = useContext(ListsContext);
  if (!context) {
    throw new Error("useLists must be used within a ListsProvider");
  }
  return { lists: context.state.lists, loading: context.state.loading, ...context };
};
